import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import api from '@/lib/api';
import { Loader2, BookOpen, ShoppingCart, Calendar, User as UserIcon, Info, Send } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

/**
 * Public book detail page. Also serves the admin preview when
 * `previewSource` prop is passed (see AdminBookPreview wrapper).
 */
export default function BookDetail({ previewSource }) {
  const { slug, bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    (async () => {
      try {
        if (previewSource === 'admin' && bookId) {
          const { data } = await api.get(`/admin/livros/preview/book/${bookId}`);
          setBook(data);
          setStatus('ready');
          return;
        }
        // Public route — first check the section gate
        const sec = await api.get('/livros/section-status');
        if (sec.data.status !== 'published') {
          setStatus('unavailable');
          return;
        }
        const { data } = await api.get(`/livros/${slug}`);
        if (data.slug && data.slug !== slug) { navigate(`/livros/${data.slug}`, { replace: true }); return; }
        setBook(data);
        setStatus('ready');
      } catch (err) {
        if (err?.response?.status === 404) setStatus('notfound');
        else setStatus('unavailable');
      }
    })();
  }, [slug, bookId, previewSource, navigate]);

  if (status === 'loading') {
    return <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-ruby" /></div></div>;
  }

  if (status !== 'ready' || !book) {
    return (
      <>
        <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div className="min-h-screen bg-background">
          <Navbar />
          <div className="max-w-lg mx-auto pt-40 px-4 text-center">
            <BookOpen className="w-12 h-12 text-ruby mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Livro não disponível</h1>
            <button onClick={() => navigate('/livros')} className="mt-6 px-4 py-2 bg-ruby rounded-md text-white">Ver Livros</button>
          </div>
        </div>
      </>
    );
  }

  const isPreview = previewSource === 'admin';
  const openPurchase = () => {
    if (!book.purchase_url) return;
    window.open(book.purchase_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Helmet>
        {isPreview && <meta name="robots" content="noindex, nofollow" />}
        <title>{book.title} — Intimate</title>
        <meta name="description" content={book.short_description || ''} />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-5xl mx-auto px-4 pt-24 pb-16" data-testid="book-detail">
          {isPreview && (
            <div className="mb-6 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 px-4 py-2 text-sm">
              👁️ Pré-visualização como visitante — status: <b>{book.status}</b>. Não indexável, não pública.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Cover */}
            <div className="md:col-span-1">
              <div className="aspect-[2/3] rounded-md overflow-hidden bg-white/5">
                {book.cover_image ? (
                  <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-12 h-12 text-muted-foreground" /></div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="md:col-span-2 space-y-4">
              <div className="flex flex-wrap gap-2 items-center">
                {book.featured && <Badge className="bg-gold text-black">Destaque</Badge>}
                {book.is_new_release && <Badge className="bg-ruby text-white">Novidade</Badge>}
                {(book.genres || []).map(g => <Badge key={g} variant="outline" className="border-border">{g}</Badge>)}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">{book.title}</h1>
              {book.author ? (
                <Link to={`/livros/autores/${book.author.slug}`} className="text-ruby hover:underline text-sm inline-flex items-center gap-1" data-testid="book-author-link">
                  <UserIcon className="w-3.5 h-3.5" /> {book.author.name}
                </Link>
              ) : book.author_name ? (
                <span className="text-muted-foreground text-sm inline-flex items-center gap-1"><UserIcon className="w-3.5 h-3.5" />{book.author_name}</span>
              ) : null}
              {book.short_description && <p className="text-muted-foreground text-lg">{book.short_description}</p>}

              {/* Meta */}
              <div className="grid grid-cols-2 gap-3 text-sm pt-2">
                {book.publisher && <div><span className="text-muted-foreground">Editora:</span> <span className="text-foreground">{book.publisher}</span></div>}
                {book.pages && <div><span className="text-muted-foreground">Páginas:</span> <span className="text-foreground">{book.pages}</span></div>}
                {book.publication_date && (
                  <div className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-muted-foreground" /><span className="text-foreground">{new Date(book.publication_date).toLocaleDateString('pt-PT')}</span></div>
                )}
                {book.formats?.length > 0 && <div><span className="text-muted-foreground">Formatos:</span> <span className="text-foreground">{book.formats.join(', ')}</span></div>}
              </div>

              {/* Contact method — either external link OR order form,
                  never both. Backend already stripped the fields that
                  don't apply, but we re-check contact_method just in case. */}
              {book.contact_method === 'form' ? (
                <OrderRequestBlock book={book} isPreview={isPreview} />
              ) : book.purchase_url && (
                <div className="pt-4 space-y-2">
                  <Button
                    onClick={openPurchase}
                    className="bg-ruby hover:bg-ruby-light text-white"
                    data-testid="book-purchase-btn"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    {book.purchase_button_label || 'Comprar este livro'}
                  </Button>
                  <p className="text-xs text-muted-foreground flex items-start gap-1.5 max-w-xl">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    A Intimate apresenta e recomenda esta obra. A compra é realizada diretamente através do autor, editora ou entidade responsável.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Synopsis */}
          {book.synopsis && (
            <section className="mt-12 max-w-3xl">
              <h2 className="text-xl font-semibold text-foreground mb-3">Sinopse</h2>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{book.synopsis}</p>
            </section>
          )}

          {book.excerpt && (
            <section className="mt-8 max-w-3xl">
              <h2 className="text-xl font-semibold text-foreground mb-3">Excerto</h2>
              <blockquote className="text-muted-foreground italic border-l-2 border-ruby pl-4 whitespace-pre-line">{book.excerpt}</blockquote>
            </section>
          )}

          {book.gallery?.length > 0 && (
            <section className="mt-8">
              <h2 className="text-xl font-semibold text-foreground mb-3">Galeria</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {book.gallery.map((src, i) => (
                  <img key={i} src={src} alt="" className="aspect-square object-cover rounded-md" />
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </>
  );
}

function OrderRequestBlock({ book, isPreview }) {
  const [open, setOpen] = useState(false);
  const hasMultipleFormats = (book.formats || []).length > 1;
  const singleFormat = (book.formats || [])[0] || '';
  const [form, setForm] = useState({
    name: '', email: '', phone: '', country: '', postal_code: '',
    preferred_format: hasMultipleFormats ? '' : singleFormat,
    quantity: 1, message: '',
    accept_privacy: false, accept_share: false,
  });
  const [sending, setSending] = useState(false);
  const fmtLower = (form.preferred_format || '').toLowerCase();
  const isPhysical = ['capa','físico','fisico','papel','impresso'].some(h => fmtLower.includes(h));
  const canSubmit = form.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
    && form.country.trim() && form.quantity >= 1
    && (!hasMultipleFormats || form.preferred_format)
    && (!isPhysical || form.postal_code.trim())
    && form.accept_privacy && form.accept_share;

  const submit = async () => {
    if (!canSubmit) { toast.error('Preenche todos os campos obrigatórios e aceita os consentimentos.'); return; }
    if (isPreview) { toast.info('Pré-visualização — pedidos não são enviados.'); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/livros/${book.slug}/order-request`, form);
      toast.success(`Pedido enviado. Referência ${data.reference}. Confirmação a caminho por email.`);
      setOpen(false);
      setForm({ name: '', email: '', phone: '', country: '', postal_code: '',
        preferred_format: hasMultipleFormats ? '' : singleFormat, quantity: 1, message: '',
        accept_privacy: false, accept_share: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao enviar o pedido.');
    } finally { setSending(false); }
  };

  return (
    <div className="pt-4 space-y-2">
      <Button onClick={() => setOpen(true)} className="bg-ruby hover:bg-ruby-light text-white" data-testid="book-order-btn">
        <Send className="w-4 h-4 mr-2" />{book.form_button_label || 'Pedir este livro'}
      </Button>
      <p className="text-xs text-muted-foreground flex items-start gap-1.5 max-w-xl">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        Ao enviares este formulário estás a realizar um pedido de encomenda. O autor, editora ou entidade responsável entrará em contacto contigo para confirmar a disponibilidade, o pagamento e a entrega.
      </p>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-border sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="order-request-dialog">
          <DialogHeader>
            <DialogTitle>Pedido de encomenda</DialogTitle>
            <DialogDescription>Preenche os dados abaixo. A Intimate reencaminha o pedido ao responsável.</DialogDescription>
          </DialogHeader>

          {/* Book summary — never editable by the reader */}
          <div className="flex gap-3 p-3 rounded-md bg-white/5 border border-border" data-testid="order-book-summary">
            <div className="w-14 h-20 rounded overflow-hidden bg-white/5 shrink-0">
              {book.cover_image ? <img src={book.cover_image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-5 h-5 text-muted-foreground" /></div>}
            </div>
            <div className="min-w-0 text-sm">
              <p className="font-semibold text-foreground truncate" data-testid="order-book-title">{book.title}</p>
              <p className="text-muted-foreground text-xs truncate" data-testid="order-book-author">{book.author?.name || book.author_name || '—'}</p>
              {(book.formats || []).length > 0 && (
                <p className="text-muted-foreground text-xs mt-0.5" data-testid="order-book-formats">Formatos: {(book.formats || []).join(', ')}</p>
              )}
              {book.indicative_price && <p className="text-xs text-gold mt-0.5">Preço indicativo: {book.indicative_price}</p>}
            </div>
          </div>

          <div className="space-y-3">
            <div><Label className="text-xs">Nome completo *</Label><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="order-name" required /></div>
            <div><Label className="text-xs">Email *</Label><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="order-email" required /></div>
            <div><Label className="text-xs">Telefone (opcional)</Label><Input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} data-testid="order-phone" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">País *</Label><Input value={form.country} onChange={e=>setForm({...form, country:e.target.value})} placeholder="Portugal" data-testid="order-country" required /></div>
              <div>
                <Label className="text-xs">Código postal{isPhysical ? ' *' : ''}</Label>
                <Input value={form.postal_code} onChange={e=>setForm({...form, postal_code:e.target.value})} placeholder="0000-000" data-testid="order-postal" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Formato{hasMultipleFormats ? ' *' : ''}</Label>
                {hasMultipleFormats ? (
                  <select value={form.preferred_format} onChange={e=>setForm({...form, preferred_format:e.target.value})} className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm" data-testid="order-format" required>
                    <option value="">— Escolhe —</option>
                    {(book.formats||[]).map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                ) : (
                  <Input value={form.preferred_format} onChange={e=>setForm({...form, preferred_format:e.target.value})} placeholder={singleFormat || 'ebook / capa mole…'} />
                )}
              </div>
              <div><Label className="text-xs">Quantidade *</Label><Input type="number" min={1} value={form.quantity} onChange={e=>setForm({...form, quantity: parseInt(e.target.value,10) || 1})} data-testid="order-quantity" required /></div>
            </div>
            <div><Label className="text-xs">Mensagem (opcional)</Label><Textarea rows={3} value={form.message} onChange={e=>setForm({...form, message:e.target.value})} data-testid="order-message" /></div>

            {/* Consent — both required */}
            <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={form.accept_privacy} onChange={e=>setForm({...form, accept_privacy:e.target.checked})} data-testid="order-privacy" className="mt-1" />
              <span>Aceito a Política de Privacidade da Intimate. *</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={form.accept_share} onChange={e=>setForm({...form, accept_share:e.target.checked})} data-testid="order-share" className="mt-1" />
              <span>Consinto que os meus dados sejam partilhados com o autor, editora ou entidade responsável, exclusivamente para dar seguimento ao pedido. *</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="order-cancel">Cancelar</Button>
            <Button onClick={submit} disabled={sending || !canSubmit} className="bg-ruby" data-testid="order-submit">
              {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enviar pedido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
